import React, { useState, useEffect } from 'react';
import axios from "axios";
import { NavLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Container,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import BusinessIcon from '@mui/icons-material/Business';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { useDirectionContext } from '../contexts/DirectionContext';
import { colors, spacing, typography, shadows, radius, transitions } from '../theme/tokens';

// Authorized phone numbers that can see organization management buttons
const AUTHORIZED_PHONES = [
    '+972548004990',
    '+972543257745',
    '0548004990',  // Development bypass format
    '0543257745'   // Development bypass format
];

const StyledAppBar = styled(AppBar)({
  backgroundColor: colors.background.paper,
  boxShadow: shadows.sm,
  borderBottom: `1px solid ${colors.border.light}`,
});

const Logo = styled('img')({
  height: '50px',
  cursor: 'pointer',
  transition: transitions.opacity,
  '&:hover': {
    opacity: 0.8,
  }
});

const NavButton = styled(Button)(({ active }) => ({
  textTransform: 'none',
  fontSize: typography.fontSize.base,
  fontWeight: active ? typography.fontWeight.semibold : typography.fontWeight.medium,
  color: active ? colors.primary.main : colors.text.secondary,
  padding: `${spacing.sm} ${spacing.md}`,
  borderRadius: radius.md,
  transition: transitions.all,
  '& .MuiButton-startIcon': {
    marginRight: spacing.sm,
    marginLeft: 0,
  },
  '&:hover': {
    backgroundColor: colors.state.hover,
    color: colors.primary.main,
  }
}));

const SignOutButton = styled(Button)({
  textTransform: 'none',
  fontSize: typography.fontSize.base,
  fontWeight: typography.fontWeight.medium,
  color: colors.error.contrast,
  backgroundColor: colors.error.main,
  padding: `${spacing.sm} ${spacing.lg}`,
  borderRadius: radius.md,
  transition: transitions.all,
  '& .MuiButton-startIcon': {
    marginRight: spacing.sm,
    marginLeft: 0,
  },
  '&:hover': {
    backgroundColor: colors.error.dark,
  }
});

const MobileDrawer = styled(Drawer)({
  '& .MuiDrawer-paper': {
    width: 280,
    padding: `${spacing.lg} 0`,
  }
});

function NavBar(props) {
    const [manager, setManager] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();
    const direction = useDirectionContext();

    // Check if current user is authorized to see org management buttons
    const isAuthorized = AUTHORIZED_PHONES.includes(props.user);

    useEffect(() => {
        async function fetchAuth() {
            try {
                const isManager = await axios.get(`${window.AppConfig.serverDomain}/api/organzations/manager/${props.user}`);
                setManager(isManager);
            } catch (err) {
                // Error handled silently - could add error reporting service here
            }
        }

        fetchAuth();
    }, [props.user]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const mobileDrawer = (
        <Box>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Logo src="/logo-transparent.png" alt="Varno - ניהול מסמכים דיגיטלי" />
            </Box>
            <List>
                <ListItem disablePadding sx={{ px: 2, mb: 2 }}>
                    <LanguageSwitcher />
                </ListItem>
                {isAuthorized && (
                    <ListItem disablePadding>
                        <ListItemButton
                            component={NavLink}
                            to="/"
                            onClick={handleDrawerToggle}
                            sx={{
                                textAlign: direction === 'rtl' ? 'right' : 'left',
                                '&:hover': { backgroundColor: colors.state.hover },
                                flexDirection: direction === 'rtl' ? 'row' : 'row-reverse',
                                justifyContent: direction === 'rtl' ? 'flex-start' : 'flex-end'
                            }}
                        >
                            <ListItemText primary={t('nav.organizations')} primaryTypographyProps={{ fontWeight: typography.fontWeight.medium }} />
                            <BusinessIcon sx={{ [direction === 'rtl' ? 'mr' : 'ml']: 2, color: colors.primary.main }} />
                        </ListItemButton>
                    </ListItem>
                )}
                {isAuthorized && manager && (
                    <ListItem disablePadding>
                        <ListItemButton
                            component={NavLink}
                            to="/create-org"
                            onClick={handleDrawerToggle}
                            sx={{
                                textAlign: direction === 'rtl' ? 'right' : 'left',
                                '&:hover': { backgroundColor: colors.state.hover },
                                flexDirection: direction === 'rtl' ? 'row' : 'row-reverse',
                                justifyContent: direction === 'rtl' ? 'flex-start' : 'flex-end'
                            }}
                        >
                            <ListItemText primary={t('nav.createOrganization')} primaryTypographyProps={{ fontWeight: typography.fontWeight.medium }} />
                            <AddCircleOutlineIcon sx={{ [direction === 'rtl' ? 'mr' : 'ml']: 2, color: colors.primary.main }} />
                        </ListItemButton>
                    </ListItem>
                )}
                <ListItem disablePadding sx={{ mt: 2, px: 2 }}>
                    <SignOutButton
                        fullWidth
                        startIcon={<LogoutIcon />}
                        onClick={props.signOut}
                    >
                        {t('nav.signOut')}
                    </SignOutButton>
                </ListItem>
            </List>
        </Box>
    );

    return (
        <StyledAppBar position="sticky">
            <Container maxWidth="xl">
                <Toolbar
                    disableGutters
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    {/* Logo - positioned based on direction */}
                    <Box sx={{ order: direction === 'rtl' ? 2 : 1 }}>
                        <NavLink to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                            <Logo src="/logo-transparent.png" alt="Varno - ניהול מסמכים דיגיטלי" />
                        </NavLink>
                    </Box>

                    {/* Desktop Navigation - positioned based on direction */}
                    {!isMobile && !props.publicRoute && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            order: direction === 'rtl' ? 1 : 2,
                            flexDirection: direction === 'rtl' ? 'row-reverse' : 'row'
                        }}>
                            {isAuthorized && (
                                <NavButton
                                    component={NavLink}
                                    to="/"
                                    {...(direction === 'rtl'
                                        ? { endIcon: <BusinessIcon /> }
                                        : { startIcon: <BusinessIcon /> }
                                    )}
                                    sx={{
                                        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                                            marginRight: '8px',
                                            marginLeft: '8px'
                                        }
                                    }}
                                >
                                    {t('nav.organizations')}
                                </NavButton>
                            )}
                            {isAuthorized && manager && (
                                <NavButton
                                    component={NavLink}
                                    to="/create-org"
                                    {...(direction === 'rtl'
                                        ? { endIcon: <AddCircleOutlineIcon /> }
                                        : { startIcon: <AddCircleOutlineIcon /> }
                                    )}
                                    sx={{
                                        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                                            marginRight: '8px',
                                            marginLeft: '8px'
                                        }
                                    }}
                                >
                                    {t('nav.createOrganization')}
                                </NavButton>
                            )}
                            <LanguageSwitcher />
                            <SignOutButton
                                {...(direction === 'rtl'
                                    ? { endIcon: <LogoutIcon /> }
                                    : { startIcon: <LogoutIcon /> }
                                )}
                                onClick={props.signOut}
                                sx={{
                                    '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                                        marginRight: '8px',
                                        marginLeft: '8px'
                                    }
                                }}
                            >
                                {t('nav.signOut')}
                            </SignOutButton>
                        </Box>
                    )}

                    {/* Mobile Menu Button - positioned based on direction */}
                    {isMobile && !props.publicRoute && (
                        <Box sx={{ order: direction === 'rtl' ? 1 : 2 }}>
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={handleDrawerToggle}
                                sx={{ color: colors.primary.main }}
                            >
                                <MenuIcon />
                            </IconButton>
                        </Box>
                    )}
                </Toolbar>
            </Container>

            {/* Mobile Drawer */}
            <MobileDrawer
                anchor={direction === 'rtl' ? 'right' : 'left'}
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
            >
                {mobileDrawer}
            </MobileDrawer>
        </StyledAppBar>
    );
}

export default NavBar;
